/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import {
  Package, Plus, Edit3, Trash2, Copy, Users, CreditCard, Wallet, Coins,
  TrendingUp, Shield, Building, Briefcase, Eye, UserPlus, Check, X,
  Loader2, Search, Filter, BarChart3, Settings, Zap, Star, Crown,
  ChevronRight, ArrowRight, Sparkles, Target, Award, Clock, Calendar,
  PieChart, Activity, Info, AlertCircle, CheckCircle2, RefreshCw, ShoppingCart, Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import StripeCheckoutButton from './StripeCheckoutButton';
import { getToken, getAuthHeader as getStoredAuthHeader } from '../services/tokenService';
import InstitutionalHero from './ui/InstitutionalHero';


export const GestionPlansPlansTab = ({ ctx }) => {
  const {
    activeTab, assignForm, assignPlanSearch, assignUserSearch, assignmentSearch,
    assignmentStatusFilter, assignments, dialogStep, editingPlan, filterRole, filterType,
    loading, planForm, plans, saving, searchTerm, selectedPlan, selectedPlanIds,
    showAssignDialog, showDetailDialog, showPlanDialog, users,
    setActiveTab, setAssignForm, setAssignPlanSearch, setAssignUserSearch, setAssignmentSearch,
    setAssignmentStatusFilter, setAssignments, setDialogStep, setEditingPlan, setFilterRole,
    setFilterType, setLoading, setPlanForm, setPlans, setSaving, setSearchTerm, setSelectedPlan,
    setSelectedPlanIds, setShowAssignDialog, setShowDetailDialog, setShowPlanDialog, setUsers,
    fetchAssignments, fetchPlans, fetchUsers, filteredPlans, getAuthHeader, getPricingTypeConfig,
    getRoleConfig, handleAssignPlan, handleBulkDeletePlans, handleBulkToggleActive, handleDeletePlan,
    handleDuplicatePlan, handleSavePlan, openCreateDialog, openDetailDialog, openEditDialog,
    renderPlanPrice, stats, togglePlanSelection, toggleSelectAllPlans,
    PRICING_TYPES, TARGET_ROLES, DEFAULT_PLAN, PLAN_COLORS, getErrorMessage,
  } = ctx;
  return (
        <TabsContent value="plans" className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="text-center">
                <Loader2 className="h-10 w-10 animate-spin text-purple-600 mx-auto mb-4" />
                <p className="text-gray-500">Chargement des plans...</p>
              </div>
            </div>
          ) : filteredPlans.length === 0 ? (
            <Card className="border-2 border-dashed border-gray-200">
              <CardContent className="py-16 text-center">
                <div className="p-4 bg-purple-100 rounded-full w-fit mx-auto mb-4">
                  <Package className="h-12 w-12 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Aucun plan trouvé</h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  {searchTerm || filterType !== 'all' 
                    ? "Aucun plan ne correspond à vos critères de recherche."
                    : "Commencez par créer votre premier plan d'abonnement pour offrir des services à vos utilisateurs."
                  }
                </p>
                <Button onClick={openCreateDialog} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Créer votre premier plan
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Bulk toolbar */}
              {selectedPlanIds.size > 0 && (() => {
                const selectedPlans = filteredPlans.filter((p) => selectedPlanIds.has(p.id));
                const anyActive = selectedPlans.some((p) => p.is_active);
                const anyInactive = selectedPlans.some((p) => !p.is_active);
                return (
                <div className="sticky top-2 z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl shadow-lg" data-testid="plans-bulk-toolbar">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-white/20 backdrop-blur">
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-bold">{selectedPlanIds.size} plan{selectedPlanIds.size > 1 ? 's' : ''} sélectionné{selectedPlanIds.size > 1 ? 's' : ''}</div>
                      <div className="text-xs text-purple-100">Activer/Désactiver préserve les abonnés · Supprimer ignore ceux avec abonnés actifs</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {anyInactive && (
                      <Button size="sm" onClick={() => handleBulkToggleActive(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="plans-bulk-activate-btn">
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Activer en masse
                      </Button>
                    )}
                    {anyActive && (
                      <Button size="sm" onClick={() => handleBulkToggleActive(false)} className="bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="plans-bulk-deactivate-btn">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        Désactiver en masse
                      </Button>
                    )}
                    <Button size="sm" onClick={handleBulkDeletePlans} className="bg-red-500 hover:bg-red-600 text-white font-semibold" data-testid="plans-bulk-delete-btn">
                      <Trash2 className="w-4 h-4 mr-1" />
                      Supprimer en masse
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedPlanIds(new Set())} className="text-white hover:bg-white/15" data-testid="plans-bulk-clear-btn">
                      <X className="w-4 h-4 mr-1" />
                      Annuler
                    </Button>
                  </div>
                </div>
                );
              })()}

              {/* Sélectionner tous + récap */}
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                    checked={filteredPlans.length > 0 && filteredPlans.every((p) => selectedPlanIds.has(p.id))}
                    onChange={toggleSelectAllPlans}
                    data-testid="plans-select-all"
                  />
                  Sélectionner tous les plans affichés ({filteredPlans.length})
                </label>
                {selectedPlanIds.size > 0 && (
                  <span className="text-xs text-purple-700 font-semibold">{selectedPlanIds.size} sélectionné{selectedPlanIds.size > 1 ? 's' : ''}</span>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredPlans.map((plan) => {
                const typeConfig = getPricingTypeConfig(plan.type_tarification);
                const TypeIcon = typeConfig.icon;
                const isSelected = selectedPlanIds.has(plan.id);

                return (
                  <Card
                    key={plan.id}
                    className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group ${!plan.is_active ? 'opacity-60' : ''} ${isSelected ? 'ring-2 ring-purple-500 ring-offset-2' : ''}`}
                    data-testid={`plan-card-${plan.id}`}
                  >
                    {/* Checkbox de sélection */}
                    <label className="absolute top-3 left-3 z-20 cursor-pointer bg-white/95 backdrop-blur rounded-md p-1 shadow-sm border border-slate-200 hover:border-purple-400 transition">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500"
                        checked={isSelected}
                        onChange={() => togglePlanSelection(plan.id)}
                        data-testid={`plan-select-${plan.id}`}
                        aria-label={`Sélectionner ${plan.nom}`}
                      />
                    </label>

                    {/* Header coloré */}
                    <div
                      className="h-2"
                      style={{ backgroundColor: plan.couleur || '#8B5CF6' }}
                    />
                    
                    {/* Badge par défaut */}
                    {plan.is_default && (
                      <div className="absolute top-4 right-4">
                        <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-0 shadow-md font-semibold">
                          <Crown className="h-3 w-3 mr-1" />
                          Par défaut
                        </Badge>
                      </div>
                    )}
                    
                    <CardHeader className="pb-3">
                      <div className="flex items-start gap-4">
                        <div 
                          className="p-3 rounded-xl shadow-md"
                          style={{ backgroundColor: `${plan.couleur}20` }}
                        >
                          <TypeIcon className="h-6 w-6" style={{ color: plan.couleur }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-xl font-bold truncate pr-16">{plan.nom}</CardTitle>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge 
                              variant="outline" 
                              className={`${typeConfig.bg} ${typeConfig.text} border-0`}
                            >
                              {typeConfig.label}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-5">
                      {/* Description */}
                      {plan.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">{plan.description}</p>
                      )}
                      
                      {/* Prix */}
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4">
                        {renderPlanPrice(plan)}
                        
                        {plan.pricing?.prix_annuel > 0 && (
                          <p className="text-sm text-gray-500 mt-2">
                            ou {plan.pricing.prix_annuel}€/an 
                            <Badge className="ml-2 bg-green-600 text-white border-0 text-xs font-semibold">
                              Économisez {Math.round((1 - plan.pricing.prix_annuel / (plan.pricing.prix_mensuel * 12)) * 100)}%
                            </Badge>
                          </p>
                        )}
                      </div>

                      {/* Rôles cibles */}
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Destiné à</p>
                        <div className="flex flex-wrap gap-1.5">
                          {(plan.roles_cibles || []).map((role) => {
                            const roleConfig = getRoleConfig(role);
                            const RoleIcon = roleConfig.icon;
                            return (
                              <Badge 
                                key={role} 
                                variant="secondary" 
                                className={`${roleConfig.bg} ${roleConfig.text} border-0 text-xs`}
                              >
                                <RoleIcon className="h-3 w-3 mr-1" />
                                {roleConfig.label}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>

                      {/* Stats et status */}
                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1.5 text-sm text-gray-600">
                            <Users className="h-4 w-4 text-purple-500" />
                            <span className="font-semibold">{plan.subscribers_count || 0}</span>
                            <span>abonné{(plan.subscribers_count || 0) > 1 ? 's' : ''}</span>
                          </span>
                        </div>
                        <Badge 
                          className={`${plan.is_active 
                            ? 'bg-emerald-600 text-white' 
                            : 'bg-gray-600 text-white'
                          } border-0`}
                        >
                          {plan.is_active ? 'Actif' : 'Inactif'}
                        </Badge>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-3 border-t">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1 hover:bg-purple-50 hover:text-purple-700 hover:border-purple-200"
                          onClick={() => openEditDialog(plan)}
                        >
                          <Edit3 className="h-4 w-4 mr-1.5" />
                          Modifier
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="hover:bg-blue-50 hover:text-blue-600"
                          onClick={() => handleDuplicatePlan(plan, true)}
                          title="Dupliquer et personnaliser"
                          data-testid={`plan-duplicate-${plan.id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="hover:bg-purple-50 hover:text-purple-600"
                          onClick={() => openDetailDialog(plan)}
                          title="Voir détails"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="hover:bg-red-50 hover:text-red-600"
                          onClick={() => handleDeletePlan(plan)}
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      {/* Bouton Souscrire avec Stripe */}
                      {plan.prix_mensuel > 0 && (
                        <StripeCheckoutButton
                          type="plan"
                          item={plan}
                          billingPeriod="monthly"
                          getAuthHeader={getAuthHeader}
                          buttonText="Souscrire"
                          buttonVariant="default"
                          className="w-full mt-2 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                        />
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              </div>
            </>
          )}
        </TabsContent>
  );
};

export default GestionPlansPlansTab;
